/* eslint-disable react/no-array-index-key */
/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable react/jsx-no-duplicate-props */
import React, { useState, ReactElement, useContext, useEffect } from 'react';
import clsx from 'clsx';
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline';
import AddIcon from '@material-ui/icons/Add';
import { Formik } from 'formik';
import { useHistory } from 'react-router-dom';
import {
  TextField,
  DialogActions,
  Grid,
  Typography,
  LinearProgress,
  Box,
  Button,
  Divider,
} from '@material-ui/core';
import {
  MediaType,
  Action,
  SelectEvent,
  ProgramFormikSubmit,
  ProgramFormFields,
  ImageAspectRatio,
  UploadedImage,
} from 'models/forms';
import { getAvailableOrders, getDurations } from 'helpers/programs';
import {
  CANCEL_PROGRAM_CONFIRMATION_TITLE,
  CANCEL_PROGRAM_CONFIRMATION_BODY,
  REMOVE_PROGRAM_CONFIRMATION_TITLE,
  REMOVE_PROGRAM_CONFIRMATION_BODY,
} from 'constants/notifications';
import { NotificationContext } from 'context/NotificationContext';
import { Program, ProgramInput, MediaFile, AudioOptionInput } from 'models';
import { containsDuplicates } from 'helpers/arrays';
import ImageUploader from 'components/form/ImageUploader';
import SecondaryButton from 'components/form/buttons/SecondaryButton';
import PrimaryButton from 'components/form/buttons/PrimaryButton';
import ConfirmationDialog from 'components/mainComponents/ConfirmationDialog';
import Select from 'components/form/Select';
import Switch from 'components/form/Switch';
import TextFieldLength from 'components/form/TextFieldLength';
import MediaUploader from 'components/form/MediaUploader';
import usePrograms from 'hooks/usePrograms';
import Option from 'components/form/AudioOption';
import { PROGRAMS } from 'constants/routes';
import { getProgramsFormNotification } from 'helpers/notifications';
import SubmitLoading from 'components/loading/SubmitLoading';
import {
  areOptionsEdited,
  areOptionsCompleted,
  handleSchemaValidation,
} from './validations';
import {
  initialOption,
  initialFileInput,
  getInitialValues,
} from './initialValues';
import useStyles from './style';

interface Props {
  program?: Program;
}

const AddNewProgramForm: React.FC<Props> = (props) => {
  const { program } = props;
  const initialValues = getInitialValues(program);
  const isEdit = program !== undefined;
  const classes = useStyles();
  const history = useHistory();
  const initDurations = program ? getDurations(program.options) : [];
  const { displayNotification } = useContext(NotificationContext);
  const { getExistingOrders, add, update, remove } = usePrograms();
  const [isCancelDialogOpen, setIsCancelDialogOpen] = useState(false);
  const [isRemoveDialogOpen, setIsRemoveDialogOpen] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [video, setVideo] = useState(initialValues.video || initialFileInput);
  const [selectedOrder, setSelectedOrder] = useState(null as null | number);
  const [existingOrders, setExistingOrders] = useState([] as number[]);
  const [selectedDurations, setSelectedDurations] = useState(
    initDurations as number[],
  );
  const [options, setOptions] = useState(
    initialValues.options || [initialOption],
  );
  const [isSubscriptionRequired, setIsSubscriptionRequired] = useState(
    initialValues.isSubscriptionRequired,
  );
  const [selectedImage, setSelectedImage] = useState(
    (program?.imageURL || null) as null | Blob | string,
  );

  const getOrdersBySubscriptionType = async (): Promise<void> => {
    setSelectedOrder(null);
    const orders = await getExistingOrders(isSubscriptionRequired);
    setExistingOrders(orders);
  };

  // Fields change handlers.
  const handleOrderSelect = (event: SelectEvent): void => {
    setSelectedOrder(event.target.value as number);
  };

  const handleImageUpload = (image: UploadedImage): void => {
    setSelectedImage(image.file);
  };

  const handleSubscriptionChange = (
    _: React.ChangeEvent<HTMLInputElement>,
    checked: boolean,
  ): void => {
    setIsSubscriptionRequired(checked);
  };

  const handleVideoUpload = (file: MediaFile): void => {
    setVideo(file);
  };

  // Duration handlers.
  const deleteDuration = (option: number): void => {
    const newDurations = selectedDurations.filter(
      (_, index) => index !== option,
    );
    setSelectedDurations(newDurations);
  };

  const updateDurationsList = (option: number, duration: number): void => {
    const newDurations = selectedDurations;
    if (newDurations.length > option) {
      newDurations[option] = duration;
    } else {
      newDurations.push(duration);
    }
    setSelectedDurations(newDurations);
  };

  // Option field handlers.
  const deleteOption = (option: number): void => {
    const newOptions = options.filter((_, index) => index !== option);
    setOptions(newOptions);
  };

  const updateOptions = (option: number, values: AudioOptionInput): void => {
    const newOptions = [...options];
    newOptions[option] = values;
    setOptions(newOptions);
  };

  const handleOptionAdd = (): void => {
    setOptions([...options, { ...initialOption }]);
  };

  const handleOptionUpdate = (
    option: number,
    values: AudioOptionInput,
  ): void => {
    updateOptions(option, values);
    if (values.duration) {
      updateDurationsList(option, values.duration);
    }
  };

  const handleOptionDelete = (option: number): void => {
    const { duration } = options[option];
    if (duration) {
      deleteDuration(option);
    }
    deleteOption(option);
  };

  // Validation.
  const isSaveDisabled = (
    isSubmitting: boolean,
    values: ProgramFormFields,
  ): boolean => {
    const optionsCompleted = areOptionsCompleted(options);
    const areDurationsDuplicated = containsDuplicates(selectedDurations);
    return (
      isSubmitting ||
      !values.name.trim() ||
      !values.description.trim() ||
      !selectedImage ||
      !optionsCompleted ||
      selectedOrder === null ||
      areDurationsDuplicated ||
      video.file === ''
    );
  };

  const isFormEdited = (values: ProgramFormFields): boolean => {
    const optionsEdited = areOptionsEdited(options);
    return (
      values.name !== '' ||
      values.description !== '' ||
      values.enabled ||
      isSubscriptionRequired ||
      selectedOrder !== null ||
      selectedImage !== null ||
      optionsEdited ||
      video.file !== ''
    );
  };

  // Actions.
  const handleCancel = (values: ProgramFormFields): void => {
    const isEdited = isFormEdited(values);
    if (isEdited) {
      setIsCancelDialogOpen(true);
    } else {
      history.push(PROGRAMS, { goBack: true });
    }
  };

  const handleRemove = (): void => {
    setIsRemoveDialogOpen(true);
  };

  const handleFormSubmit = async (
    values: ProgramFormFields,
    helpers: ProgramFormikSubmit,
  ): Promise<void> => {
    let action = Action.ADD;
    let result = false;
    helpers.setSubmitting(true);
    setIsProcessing(true);
    const newProgram: ProgramInput = {
      name: values.name,
      description: values.description,
      order: selectedOrder || 0,
      enabled: values.enabled,
      isSubscriptionRequired,
      imageURL: selectedImage as Blob,
      options,
      video,
    };
    if (program) {
      action = Action.UPDATE;
      result = await update(program, newProgram);
    } else {
      result = await add(newProgram);
    }
    const notification = getProgramsFormNotification(result, action);
    displayNotification(notification.status, notification.message);
    history.push(PROGRAMS, { goBack: action === Action.UPDATE });
  };

  const handleCancelConfirm = (): void => {
    history.push(PROGRAMS, { goBack: true });
  };

  const handleCancelDismiss = (): void => {
    setIsCancelDialogOpen(false);
  };

  const handleRemoveConfirm = async (): Promise<void> => {
    setIsRemoveDialogOpen(false);
    setIsProcessing(true);
    if (program?.id) {
      const removed = await remove(program);
      setIsProcessing(false);
      const notification = getProgramsFormNotification(removed, Action.REMOVE);
      displayNotification(notification.status, notification.message);
      history.goBack();
    }
  };

  const handleRemoveDismiss = (): void => {
    setIsRemoveDialogOpen(false);
  };

  useEffect(() => {
    getOrdersBySubscriptionType();
  }, [isSubscriptionRequired]);

  useEffect(() => {
    const { order } = initialValues;
    const totalOrders = existingOrders.length;
    const currentOrder = order !== null && totalOrders > order ? order : null;
    setSelectedOrder(currentOrder);
  }, [existingOrders]);

  return (
    <>
      <SubmitLoading isOpen={isProcessing} />
      <Formik
        initialValues={initialValues}
        onSubmit={handleFormSubmit}
        validationSchema={handleSchemaValidation}
      >
        {(formProps): ReactElement => {
          const {
            values,
            touched,
            errors,
            isSubmitting,
            handleChange,
            handleSubmit,
          } = formProps;
          return (
            <form
              className={clsx(classes.content, {
                [classes.overlay]: isSubmitting || isProcessing,
              })}
              onSubmit={handleSubmit}
            >
              <Grid container className={classes.topRow}>
                <Grid item xs={12} sm={6}>
                  <div className={classes.inlineFields}>
                    <div className={classes.textFieldContainer}>
                      <Typography variant='body2' className={classes.subTitle}>
                        Name
                      </Typography>
                      <TextField
                        required
                        name='name'
                        value={values.name}
                        onChange={handleChange}
                        helperText={errors.name && touched.name && errors.name}
                        InputProps={{
                          disableUnderline: true,
                          className: classes.textField,
                        }}
                      />
                    </div>
                    <Box className={classes.orderSelect}>
                      <Typography variant='body2' className={classes.subTitle}>
                        Order Number
                      </Typography>
                      <Select
                        id='order'
                        name='order'
                        value={selectedOrder}
                        options={getAvailableOrders(existingOrders, isEdit)}
                        onChange={handleOrderSelect}
                      />
                    </Box>
                  </div>
                  <TextField
                    required
                    multiline
                    rows={4}
                    name='description'
                    label='Description'
                    variant='filled'
                    value={values.description}
                    helperText={
                      errors.description &&
                      touched.description &&
                      errors.description
                    }
                    className={classes.textFieldMultiline}
                    inputProps={{ maxLength: 100 }}
                    onChange={handleChange}
                    InputProps={{
                      disableUnderline: true,
                      className: classes.inputProps,
                    }}
                  />
                  <TextFieldLength text={values.description} maxLength={100} />
                </Grid>
                <Grid item xs={12} sm={6} className={classes.switchesContainer}>
                  <Box className={classes.switch}>
                    <Switch
                      name='enabled'
                      label='Enabled'
                      isChecked={values.enabled}
                      onChange={handleChange}
                    />
                  </Box>
                  <Box className={classes.switch}>
                    <Switch
                      name='isSubscriptionRequired'
                      label='Subscription Required'
                      isChecked={isSubscriptionRequired}
                      onChange={handleSubscriptionChange}
                    />
                  </Box>
                </Grid>
              </Grid>
              <Divider className={classes.divider} />
              <div>
                <Typography variant='caption' className={classes.sectionTitle}>
                  Audio
                </Typography>
                {options.map((option, index) => (
                  <Option
                    key={index}
                    option={index}
                    values={option}
                    selectedDurations={selectedDurations}
                    onChange={handleOptionUpdate}
                    onDelete={handleOptionDelete}
                  />
                ))}
                {options.length < 3 && (
                  <Box
                    className={classes.addOptionContainer}
                    onClick={handleOptionAdd}
                  >
                    <Typography
                      variant='body2'
                      className={classes.addOptionButton}
                    >
                      Add Option
                    </Typography>
                    <AddIcon className={classes.addOptionIcon} />
                  </Box>
                )}
              </div>
              <Divider className={classes.divider} />
              <Grid container className={classes.bottomRow}>
                <Grid item xs={12} sm={4}>
                  <Typography variant='body2' className={classes.subTitle}>
                    Image
                  </Typography>
                  <ImageUploader
                    imageURL={program?.imageURL}
                    aspectRatio={ImageAspectRatio.AR_2_3}
                    onChange={handleImageUpload}
                  />
                  <Typography variant='body2' className={classes.maxTextLength}>
                    Min. image size 800x1200
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={8}>
                  <Typography variant='body2' className={classes.subTitle}>
                    Video
                  </Typography>
                  <MediaUploader
                    id='program-video-input'
                    buttonText='Upload Video'
                    mediaType={MediaType.VIDEO}
                    file={video}
                    onChange={handleVideoUpload}
                  />
                </Grid>
              </Grid>
              <DialogActions
                className={clsx(classes.dialogActions, {
                  [classes.editActions]: isEdit,
                })}
              >
                {isEdit && (
                  <Button
                    color='primary'
                    startIcon={<DeleteOutlineIcon />}
                    onClick={handleRemove}
                    className={classes.deleteButton}
                  >
                    Delete Program
                  </Button>
                )}
                <Box>
                  <SecondaryButton
                    onClick={(): void => handleCancel(values)}
                    disabled={isSubmitting}
                  >
                    Cancel
                  </SecondaryButton>
                  <PrimaryButton
                    type='submit'
                    onClick={handleSubmit}
                    disabled={isSaveDisabled(isSubmitting, values)}
                  >
                    Save
                  </PrimaryButton>
                </Box>
              </DialogActions>
              {(isSubmitting || isProcessing) && (
                <LinearProgress className={classes.progress} />
              )}
            </form>
          );
        }}
      </Formik>
      <ConfirmationDialog
        isOpen={isCancelDialogOpen}
        title={CANCEL_PROGRAM_CONFIRMATION_TITLE}
        message={CANCEL_PROGRAM_CONFIRMATION_BODY}
        dismissButtonText='Nevermind'
        confirmButtonText='Cancel'
        dismissButtonAction={handleCancelDismiss}
        confirmButtonAction={handleCancelConfirm}
      />
      <ConfirmationDialog
        isOpen={isRemoveDialogOpen}
        title={REMOVE_PROGRAM_CONFIRMATION_TITLE}
        message={REMOVE_PROGRAM_CONFIRMATION_BODY}
        dismissButtonText='Nevermind'
        confirmButtonText='Delete'
        dismissButtonAction={handleRemoveDismiss}
        confirmButtonAction={handleRemoveConfirm}
      />
    </>
  );
};

export default AddNewProgramForm;
